import CryptoJS from 'crypto-js'
import { logger } from 'helpers/logger'
import MarkdownIt from 'markdown-it'
import { NestedDict, Location } from 'app/types'
import ReactDOMServer from 'react-dom/server'
import ReactDOM from 'react-dom'
import React from 'react'
import { cloneDeep } from 'lodash'
import { SnapDialog } from './SnapDialog'

const mdParser = new MarkdownIt(/* Markdown-it options */);

/**
 * Strings
 */
String.prototype.toTitleCase = function (): string {
    return this.charAt(0).toUpperCase() + this.slice(1)
}

// Put space between camelCase words
String.prototype.toReadable = function (): string {
    return this.replace(/([a-z])([A-Z])/g, '$1 $2').toTitleCase()
}

String.prototype.simplify = function (): string {
    return this.toLowerCase().replace(/[^a-zA-Z0-9]/g, '')
}

String.prototype.toAlphanum = function (): string {
    return this.replace(/[^a-z0-9]/gi, '')
}

String.prototype.toNumeric = function (): string {
    let x = this.replace(/\D/g, "")
    if (x.length > 0) {
        const ret = parseFloat(x)
        if (ret != NaN) {
            return ret
        }
    }
    return 0
}

String.prototype.toCamelCase = function (): string {
    return this.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, function (match, chr) {
        return chr.toUpperCase()
    })
}

String.prototype.singular = function (): string {
    // Valid words with es plural
    if (this.endsWith('es') && ['h'].includes(this[this.length - 3])) {
        return this.substring(0, this.length - 2)
    } else if (this[this.length - 1] == 's') {
        return this.substring(0, this.length - 1)
    }
    return this
}

String.prototype.plural = function (): string {
    const lastChar = this[this.length - 1]
    if (lastChar != 's') {
        if (lastChar == 'y') {
            return `${this.substring(0, this.length - 1)}ies`
        } else if (lastChar != 'd') {
            return this + 's'
        }
    }
    return this
}

String.prototype.hashCode = function () {
    var hash = 0, i, chr
    if (this.length === 0) return hash
    for (i = 0; i < this.length; i++) {
        chr = this.charCodeAt(i)
        hash = ((hash << 5) - hash) + chr
        hash |= 0 // Convert to 32bit integer
    }
    return hash
}

String.prototype.fnv1aHash = function () {
    const str = this
    let hash = 0x811c9dc5 // 32-bit FNV-1a offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i)
        hash = Math.imul(hash, 0x01000193) // Multiply by FNV prime
    }
    return (hash >>> 0).toString(36).padStart(6, '0').slice(0, 6)
}

String.prototype.capitalize = function () {
    return this.replace(/\b[a-z]/g, match => match.toUpperCase())
}

String.prototype.makeFirstCharacterLowercase = function () {
    return this.charAt(0).toLowerCase() + this.slice(1)
}

String.prototype.toMarkdown = function () {
    return this.replace(/\\n/g, "\n")
}

// Removed duplicate method

export function toMoney(x: string, cents = true, sign = true, conditionalCents = true): string {
    let num = x ? parseFloat(x) : 0
    let val = null
    if (!x) {
        val = '$0.00'
    } else {
        val = x.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    }
    let ret = ''
    if (!cents || (conditionalCents && num - Math.floor(num) == 0)) {
        ret = val.split('.')[0]
    } else {
        ret = val
    }
    if (!sign) {
        return ret.replace('$', '')
    }
    return ret
}

export function roundCents(value) {
    if (value) {
        return Math.round(value * 100) / 100
    }
    return value
}

export function numberWithCommas(x: number): string {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export function numberWithOrdinalSuffix(i: number): string {
    let j = i % 10
    let k = i % 100
    if (j === 1 && k !== 11) {
        return i + 'st'
    }
    if (j === 2 && k !== 12) {
        return i + 'nd'
    }
    if (j === 3 && k !== 13) {
        return i + 'rd'
    }
    return i + 'th'
}

export function snakeToCamelCase(str: string) {
    return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

export function camelToSnakeCase(str: string) {
    return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

export function camelToHyphenCase(str: string) {
    return str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase()
}

export function camelToTitleCase(str: string) {
    return str.replace(/[A-Z]/g, letter => ` ${letter.toLowerCase()}`).toTitleCase()
}

export function phoneNumber(phoneNumberString: string) {
    var cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    var match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        var intlCode = (match[1] ? '+1 ' : '')
        return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }
    return null
}

export function trimTrailingSlash(x: string) {
    if (x.length > 0 && x[x.length - 1] == '/') {
        return x.slice(0, -1)
    }
    return x
}

/**
 * Numbers
 */
export function lerp(a: number, b: number, c: number): number {
    return a + (b - a) * c
}

export function lerp2D(a: number, b: number, c: number): number {
    return [a[0] + (b[0] - a[0]) * c, a[1] + (b[1] - a[1]) * c]
}

export function clamp(number: number, min: number, max: number): number {
    return Math.max(min, Math.min(number, max))
}

export function calcVelocity(a: number, b: number, dt: number): number {
    return (b - a) / dt
}

export function calcVelocity2D(a: number, b: number, dt: number): number[] {
    return [(b[0] - a[0]) / dt, (b[1] - a[1]) / dt]
}

export function degToRad(x) {
    return x * Math.PI / 180
}

export function radToDeg(x) {
    return x * 180 / Math.PI
}

export function rectContains(rect: { x: number, y: number, width: number, height: number }, pos: { x: number, y: number }) {
    return pos.x >= rect.x && pos.x <= rect.x + rect.width && pos.y > rect.y && pos.y <= rect.y + rect.height
}

/**
 * Date / time
 */
const monthStrings = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const dayStrings = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

Date.prototype.addDays = function (days: date) {
    var date = new Date(this.valueOf())
    date.setDate(date.getDate() + days)
    return date
}

Date.prototype.toTimestamp = function (date: Date) {
    let model = 'YYYYMMDD-HHmmSS'
    const language = 'en-US'
    model = model.replace('YYYY', date.getFullYear())
    model = model.replace('MM', (date.getMonth() + 1).toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('DD', date.getDate().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('HH', date.getHours().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('mm', date.getMinutes().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('SS', date.getSeconds().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    return model
}

Date.prototype.toMySQLDate = function () {
    return this.toISOString().slice(0, 19).replace('T', ' ')
}

Date.prototype.toBeginningOfDay = function () {
    let date = new Date(this)
    date.setHours(0, 0, 0, 0)
    return date
}

Date.prototype.toEndOfDay = function () {
    let date = new Date(this)
    date.setHours(23, 59, 59, 999)
    return date
}

export function datetime() {
    return (new Date()).toISOString()
}

export function dateOrdinal(d: number): string {
    if (d > 3 && d < 21) return 'th'
    switch (d % 10) {
        case 1: return 'st'
        case 2: return 'nd'
        case 3: return 'rd'
        default: return 'th'
    }
}

export function formatDate(date) {
    const options = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
    }

    const formatter = new Intl.DateTimeFormat('en-US', options)
    const parts = formatter.formatToParts(date)

    const day = parts.find(part => part.type === 'day').value
    const month = parts.find(part => part.type === 'month').value
    const year = parts.find(part => part.type === 'year').value
    const hour = parts.find(part => part.type === 'hour').value
    const minute = parts.find(part => part.type === 'minute').value
    const second = parts.find(part => part.type === 'second').value
    const dayPeriod = parts.find(part => part.type === 'dayPeriod').value
    return `${day}/${month}/${year} ${hour}:${minute}:${second} ${dayPeriod}`
}

interface DateFriendlyOptions {
    days: boolean
    time: boolean
    dayOfTheWeek: boolean
    shortMonth: boolean
}

export function dateFriendly(date: Date, dateOptions: DateFriendlyOptions) {
    if (!date) {
        return null
    }
    const options = {
        ...{ days: true, time: false, dayOfTheWeek: false, shortMonth: false, shortDayOfTheWeek: false },
        ...dateOptions
    }
    const year = date.getFullYear()
    let month = monthStrings[date.getMonth()]
    if (options.shortMonth) {
        month = month.slice(0, 3)
    }
    let ret = ''
    if (options.days) {
        const day = date.getDate()
        if (options.time) {
            ret = `${month} ${day}${dateOrdinal(day)}, ${year} ${formatAMPM(date)}`
        } else {
            ret = `${month} ${day}${dateOrdinal(day)}, ${year}`
        }
    } else {
        ret = `${month}, ${year}`
    }
    if (options.dayOfTheWeek) {
        let d = dayStrings[date.getDay()]
        if (options.shortDayOfTheWeek) {
            d = d.slice(0, 3)
        }
        ret = `${d} ${ret}`
    }
    return ret
}

export function formatAMPM(date: Date) {
    let hours = date.getHours()
    let minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'pm' : 'am'
    hours %= 12
    hours = hours > 0 ? hours : 12 // the hour '0' should be '12'
    minutes = minutes < 10 ? `0${minutes}` : minutes
    const strTime = `${hours}:${minutes} ${ampm}`
    return strTime
}

export function timestamp(format = '', language = 'en-US'): string {
    const dt = new Date()
    let model = 'YYYYMMDD-HHmmSS'
    if (format !== '') {
        model = format
    }
    model = model.replace('YYYY', dt.getFullYear())
    model = model.replace('MM', (dt.getMonth() + 1).toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('DD', dt.getDate().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('HH', dt.getHours().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('mm', dt.getMinutes().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    model = model.replace('SS', dt.getSeconds().toLocaleString(language, { minimumIntegerDigits: 2, useGrouping: false }))
    return model
}

export function dbDateToDate(dateString: string): Date {
    const date = dateString.split('T')[0]
    const time = dateString.split('T')[1].match(/.{1,2}/g).join(':')
    return new Date(`${date}T${time}Z`)
}

export function dateToDBDate(date: Date): string {
    return date.toISOString().slice(0, 19).replace('T', ' ')
}

export function formatDateToYYYYMMDD(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

export function extractDateFromFilename(filename) {
    const dateFormats = [
        /.*(\d{4})-(\d{2})-(\d{2}).*/,        // YYYY-MM-DD
        /.*(\d{2})-(\d{2})-(\d{4}).*/,        // DD-MM-YYYY
        /.*(\d{4})\.(\d{2})\.(\d{2}).*/,      // YYYY.MM.DD
        /.*(\d{2})\.(\d{2})\.(\d{4}).*/,      // DD.MM.YYYY
        /.*(\d{4})_(\d{2})_(\d{2}).*/,        // YYYY_MM_DD
        /.*(\d{2})_(\d{2})_(\d{4}).*/,        // DD_MM_YYYY
        /.*(\d{4})\/(\d{2})\/(\d{2}).*/,      // YYYY/MM/DD
        /.*(\d{2})\/(\d{2})\/(\d{4}).*/,      // DD/MM/YYYY
        /.*(\d{4})\-(\d{1,2})\-(\d{1,2}).*/,  // YYYY-M-D or YYYY-MM-D or YYYY-M-DD
        /.*(\d{4})(\d{2})(\d{2}).*/           // YYYYMMDD (no separators)
    ]

    for (const format of dateFormats) {
        const match = filename.match(format)
        if (match) {
            const year = match[1]
            const month = match[2]
            const day = match[3]

            const extractedDate = new Date(`${year}-${month}-${day}`)
            return extractedDate
        }
    }

    return null
}

export function toUtcDateString(dateString) {
    return
    // Check if the date string includes a time part
    if (!dateString.includes('T')) {
        // If not, add a default time part and set it to UTC
        dateString += 'T00:00:00Z'
    }
    return new Date(dateString)
}

export function parseLocalDate(dateString) {
    if (typeof dateString != 'string') {
        return dateString
    }
    if (!dateString) {
        return null
    }
    // Split the date string into components
    const [year, month, day] = dateString.split('-').map(Number)

    // Create a new Date object with the extracted components
    // Note: Months are zero-indexed in JavaScript Date (0 for January, 11 for December)
    return new Date(year, month - 1, day)
}

/**
 * Sets
 */
Set.prototype.difference = function (other: Set) {
    return new Set([...this].filter((x) => !other.has(x)))
}

Set.prototype.intersection = function (other: Set) {
    return new Set([...this].filter((x) => other.has(x)))
}

Set.prototype.union = function (other: Set) {
    return new Set([...this, ...other])
}

/**
 * Arrays
 */
Array.prototype.interleave = function (thing) {
    return [].concat(...this.map((n, ix) => [n, thing(ix)])).slice(0, -1)
}

Array.prototype.distinct = function () {
    return [...new Set(this)]
}

/**
 * JSON / Object
 */
export function copyObj<Type>(obj: Type): Type {
    return cloneDeep(obj)
    // return JSON.parse(JSON.stringify(obj))
}

export function hashObj(obj): string {
    return encrypt(JSON.stringify(obj))
}

export function unHashObj(str: string): NestedDict {
    try {
        return JSON.parse(decrypt(str))
    } catch (e) {
        logger.error('Failed to decode obj', e)
        return null
    }
}

export function filterObject(obj, keysToKeep) {
    if (Array.isArray(obj)) {
        return obj.map(item => filterObject(item, keysToKeep))
    } else if (obj !== null && typeof obj === "object") {
        return Object.fromEntries(
            Object.entries(obj)
                .filter(([key]) => keysToKeep.includes(key))
                .map(([key, value]) => [
                    key, 
                    value !== null && typeof value === "object" 
                        ? filterObject(value, keysToKeep) 
                        : value
                ])
        )
    }
    return obj
}

export function replaceObjectValue(obj, keyToReplace, newValue) {
    if (Array.isArray(obj)) {
        return obj.map(item => replaceObjectValue(item, keyToReplace, newValue))
    } else if (obj !== null && typeof obj === "object") {
        return Object.fromEntries(
            Object.entries(obj).map(([key, value]) => [
                key,
                key === keyToReplace
                    ? newValue
                    : (value !== null && typeof value === "object"
                        ? replaceObjectValue(value, keyToReplace, newValue)
                        : value)
            ])
        )
    }
    return obj
}

export function objIdMap(obj, field = null, altId = null) {
    if (field) {
        return obj.reduce((a, x) => ({ ...a, [altId ? x[altId] : x.id]: x[field] }), {})
    }
    return obj.reduce((a, x) => ({ ...a, [altId ? x[altId] : x.id]: x }), {})
}

export function parseCSV(csv) {
    // Strip \r characters
    csv = csv.replace(/\r/g, '')
    const lines = csv.split('\n')
    // Detect delimiter
    const delimiter = lines[0].indexOf(',') > -1 ? ',' : '\t'

    const parseCSVRow = (row) => {
        const values = []
        let currentValue = ''
        let insideQuotedValue = false

        for (let i = 0; i < row.length; i++) {
            const char = row[i]

            if ((delimiter == ',' && char === '"') && (!insideQuotedValue || i == row.length - 1 || row[i + 1] == delimiter)) {
                insideQuotedValue = !insideQuotedValue
            } else if (char === delimiter && !insideQuotedValue) {
                values.push(currentValue.trim())
                currentValue = ''
                continue
            }

            currentValue += char
        }

        values.push(currentValue.trim())

        return values
    }

    const stripQuotes = (x) => {
        if (x && x.length > 0 && ((x[0] == '"' && x[x.length - 1] == '"') || (x[0] == "'" && x[x.length - 1] == "'"))) {
            return x.substring(1, x.length - 1)
        }
        return x
    }

    const headers = parseCSVRow(lines[0])

    const data = []
    for (let i = 1; i < lines.length; i++) {
        const values = parseCSVRow(lines[i])
        // const values = lines[i].split(delimiter)

        const obj = {}
        for (let j = 0; j < headers.length; j++) {
            const header = stripQuotes(headers[j].makeFirstCharacterLowercase())
            const value = stripQuotes(values[j])
            if (!(header in obj)) {
                obj[header] = value
            }
        }

        data.push(obj)
    }

    return data
}

/**
 * Generators
 */
export function uniqueId(len: number = null): string {
    const ret = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8)
        return v.toString(16)
    })
    if (len != null) {
        return ret.substring(0, len)
    }
    return ret
}

export function generatePassword(): string {
    return Math.random().toString(36).slice(-8)
}

export function randomGenerator(s) {
    const mask = 0xffffffff
    let m_w = (123456789 + s) & mask
    let m_z = (987654321 - s) & mask

    return function () {
        m_z = (36969 * (m_z & 65535) + (m_z >>> 16)) & mask
        m_w = (18000 * (m_w & 65535) + (m_w >>> 16)) & mask

        let result = ((m_z << 16) + (m_w & 65535)) >>> 0
        result /= 4294967296
        return result
    }
}

/**
 * Colors
 */
export function stringToRgb(str: string) {
    return hexToRgb(`#${str}`)
}

export function rgbToHex(r: number, g: number, b: number) {
    // return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    const rgb = b | (g << 8) | (r << 16)
    return `#${(0x1000000 + rgb).toString(16).slice(1)}`
}

export function hexToRgb(hex: string) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
    const fixedHex = hex.replace(
        shorthandRegex,
        (m: string, r: string, g: string, b: string) => r + r + g + g + b + b
    )

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fixedHex)
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null
}

export function adjustHex(hex: string, scale: number) {
    const rgb = adjustRgb(hexToRgb(hex), scale)
    return rgbToHex(rgb.r, rgb.g, rgb.b)
}

export function adjustRgb(rgb: { [key: string]: number }, percent: number) {
    // Lighten
    if (percent > 0) {
        return {
            r: rgb.r + (255 - rgb.r) * (percent / 100),
            g: rgb.g + (255 - rgb.g) * (percent / 100),
            b: rgb.b + (255 - rgb.b) * (percent / 100)
        }
    }

    // Darken
    return {
        r: rgb.r * ((100 + percent) / 100),
        g: rgb.g * ((100 + percent) / 100),
        b: rgb.b * ((100 + percent) / 100)
    }
}

/**
 * DOM
 */
export function setRootProperty(variable: string, value) {
    document.documentElement.style.setProperty(variable, value)
}

export function getRootProperty(variable: string, element = null) {
    if (element) {
        return getComputedStyle(element).getPropertyValue(variable)
    } else {
        return getComputedStyle(document.documentElement).getPropertyValue(variable)
    }
}

export function getAppZoom(): number {
    // Get the current zoom value from CSS custom property
    // Check the #app element first since that's where --zoom is set for mobile
    const appElement = document.getElementById('app')
    let zoomValue = 1
    
    if (appElement) {
        const appZoom = parseFloat(getComputedStyle(appElement).getPropertyValue('--zoom'))
        if (appZoom) {
            zoomValue = appZoom
        }
    }
    
    // Fallback to document root if not found on app element
    if (zoomValue === 1) {
        const rootZoom = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--zoom'))
        if (rootZoom) {
            zoomValue = rootZoom
        }
    }
    
    return zoomValue
}

export async function downloadURI(uri: string, name: string) {
    try {
        const response = await fetch(uri)
        if (!response.ok) {
            logger.error(`Failed to download ${name}`)
            return
        }
        const blob = await response.blob()
        const blobUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = name
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
    } catch (error) {
        logger.error(`Error downloading ${name}:`, error)
    }
}

export async function downloadMultipleURIs(files: Array<{ uri: string; name: string }>, delay: number = 100) {
    for (let i = 0; i < files.length; i++) {
        const file = files[i]
        setTimeout(async () => {
            try {
                const response = await fetch(file.uri)
                if (!response.ok) {
                    logger.error(`Failed to download ${file.name}`)
                    return
                }
                const blob = await response.blob()
                const blobUrl = URL.createObjectURL(blob)
                
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = file.name
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                
                // Clean up the blob URL after a delay
                setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
            } catch (error) {
                logger.error(`Error downloading ${file.name}:`, error)
            }
        }, i * delay)
    }
}

// Javascript function to open a link in a new window
export function openLinkInNewWindow(uri: string) {
    const win = window.open(uri, '_blank')
    win.focus()
}

export function openHTMLInNewWindow(html) {
    // Create a new window and write the HTML content to it
    const win = window.open('', '_blank')
    win.document.write(html)
    win.focus()
}

export function openLinkInNewWindowWithDownload(uri: string, fileName: string) {
    // Create a link element
    const link = document.createElement('a')

    // Set the download attribute and href
    link.setAttribute('download', fileName)
    link.setAttribute('href', uri)
    link.setAttribute('target', '_blank')
    link.click()
}

export function pickFile() {
    return new Promise((resolve, reject) => {
        const input = document.createElement("input")
        input.type = "file"
        input.accept = "image/*" // Accept only image files (adjust as needed)

        input.onchange = (event) => {
            const file = event.target.files[0]
            if (file) {
                resolve(file)
            } else {
                reject(new Error("No file selected"))
            }
        }

        input.click() // Open the file dialog
    })
}

export function isMobileDevice() {
    return (
        /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent) || 
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 1) ||  // Detect touch support
        (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)  // Detect coarse pointer
    )
}

export function isTouchDevice() {
    return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0))
}

export function copyToClipboard(input: string) {
    const el = document.createElement('textarea')
    el.value = input

    // Prevent keyboard from showing on mobile
    el.setAttribute('readonly', '')

    el.style.contain = 'strict'
    el.style.position = 'absolute'
    el.style.left = '-9999px'
    el.style.fontSize = '12pt' // Prevent zooming on iOS

    const selection = document.getSelection()
    let originalRange = false
    if (selection.rangeCount > 0) {
        originalRange = selection.getRangeAt(0)
    }

    document.body.appendChild(el)
    el.select()

    // Explicit selection workaround for iOS
    el.selectionStart = 0
    el.selectionEnd = input.length

    let success = false
    try {
        success = document.execCommand('copy')
    } catch (err) { }

    document.body.removeChild(el)

    if (originalRange) {
        selection.removeAllRanges()
        selection.addRange(originalRange)
    }

    return success
}

export function changeFavicon(url: string) {
    const oldIcon = document.getElementById('dynamic-favicon')
    // const oldIcon = document.querySelector('link[rel="icon"]')
    const newIcon = document.createElement('link')
    newIcon.id = 'dynamic-favicon'
    newIcon.rel = 'shortcut icon'
    newIcon.type = 'image/png'
    newIcon.href = `${url}?`
    logger.info(`Change favicon: ${url}`)
    if (oldIcon) {
        document.head.removeChild(oldIcon)
    }
    document.head.appendChild(newIcon)
}

export function loadScript(id: string, url: string) {
    // logger.info(`Load script ${url}`)
    return new Promise((resolve, reject) => {
        const existingScript = document.getElementById(id)
        if (!existingScript) {
            const script = document.createElement('script')
            script.src = url
            script.id = id
            script.async = !0
            script.defer = !0
            document.body.appendChild(script)
            script.onload = resolve
            script.onerror = reject
        } else {
            resolve(true)
        }
    })
        .catch((x) => {
            logger.error(x)
            return x
        })
}

export function toggleFullscreen(element) {
    // if already full screen; exit
    // else go fullscreen
    if (document.fullscreenElement
        || document.webkitFullscreenElement
        || document.mozFullScreenElement
        || document.msFullscreenElement
    ) {
        if (document.exitFullscreen) {
            document.exitFullscreen()
        } else if (document.mozCancelFullScreen) {
            document.mozCancelFullScreen()
        } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
            document.msExitFullscreen()
        }
    } else if (element) {
        if (element.requestFullscreen) {
            element.requestFullscreen()
        } else if (element.mozRequestFullScreen) {
            element.mozRequestFullScreen()
        } else if (element.webkitRequestFullscreen) {
            element.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT)
        } else if (element.msRequestFullscreen) {
            element.msRequestFullscreen()
        }
    }
}

export function isFullscreen() {
    return document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement
}

// Function to attach hover events and display tooltips
export function attachTooltip(targetElement, tooltipText, tooltipClass) {
    if (!targetElement) {
        console.error("Element not found.")
        return
    }

    let tooltipElement

    const handleMouseEnter = (e) => {
        if (!tooltipElement) {
            tooltipElement = document.createElement("div")
            tooltipElement.className = `tooltip animate__animated animate__zoomIn animate__fastest${tooltipClass ? ` ${tooltipClass}` : ''}`
            const tooltipTextElement = document.createElement("span")
            let markdownHtml = mdParser.render(tooltipText)
            tooltipTextElement.innerHTML = markdownHtml
            tooltipElement.appendChild(tooltipTextElement)
            document.body.appendChild(tooltipElement)
        }

        const targetRect = targetElement.getBoundingClientRect()
        const tooltipWidth = tooltipElement.offsetWidth
        const tooltipHeight = tooltipElement.offsetHeight

        // Calculate tooltip position
        const left = targetRect.left + targetRect.width
        const top = targetRect.top - tooltipHeight / 2 + targetRect.height / 2

        // Set tooltip position and display it
        tooltipElement.style.left = `${left}px`
        tooltipElement.style.top = `${top}px`
        tooltipElement.style.display = "block"
    }
    
    const handleMouseLeave = (e) => {
        // Hide and remove the dynamically created tooltip on mouse leave
        if (tooltipElement) {
            tooltipElement.style.display = "none"
            tooltipElement.remove()
            tooltipElement = null
        }
    }

    targetElement.addEventListener("mouseenter", handleMouseEnter)
    targetElement.addEventListener("mouseleave", handleMouseLeave)

    return () => {
        targetElement.removeEventListener("mouseenter", handleMouseEnter)
        targetElement.removeEventListener("mouseleave", handleMouseLeave)
    }
}

export function attachTooltipDocument(buttonEvent: React.MouseEvent, tooltipText: string, tooltipClass?: string) {
    let tooltipElement: HTMLElement | null

    const handleMouseLeave = () => {
        if (tooltipElement) {
            tooltipElement.style.display = 'none'
            ReactDOM.unmountComponentAtNode(tooltipElement)
            tooltipElement.remove()
            tooltipElement = null
        }
        buttonEvent.currentTarget.removeEventListener('mouseleave', handleMouseLeave)
    }

    if (!tooltipElement) {
        tooltipElement = document.createElement('div')
        tooltipElement.className = `tooltip animate__animated animate__zoomIn animate__fastest${tooltipClass ? ` ${tooltipClass}` : ''}`

        ReactDOM.render(
            React.createElement(
                'div',
                null,
                React.createElement('span', {
                    dangerouslySetInnerHTML: { __html: mdParser.render(tooltipText) }
                })
            ),
            tooltipElement
        )

        document.body.appendChild(tooltipElement)

        const buttonRect = (buttonEvent.currentTarget as HTMLElement).getBoundingClientRect()
        const tooltipWidth = tooltipElement.offsetWidth
        const tooltipHeight = tooltipElement.offsetHeight

        // Calculate fixed tooltip position relative to the button
        const left = buttonRect.left + buttonRect.width
        const top = buttonRect.top + window.scrollY - tooltipHeight / 2 + buttonRect.height / 2

        tooltipElement.style.left = `${left}px`
        tooltipElement.style.top = `${top}px`
        tooltipElement.style.position = 'absolute'
        tooltipElement.style.display = 'block'

        buttonEvent.currentTarget.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
        if (tooltipElement) handleMouseLeave()
    }
}

/**
 * Algorithms
 */
export function sortedIndex<Type>(array: Type[], value: Type, cmp: () => number) {
    let low = 0
    let high = array.length

    while (low < high) {
        const mid = (low + high) >>> 1
        if (cmp(array[mid], value) > 0) {
            low = mid + 1
        } else {
            high = mid
        }
    }
    return low
}

// Sort A according to ids in B
export function mirrorSort<Type>(A: Type[], B: Type[], key: string) {
    // Rorder according to incoming id list
    var map: { [key: string]: Type } = {}
    for (let i = 0; i < B.length; i += 1) {
        map[B[i]] = i
    }
    A.sort((a, b) => {
        if (key) {
            return map[a[key]] - map[b[key]]
        }
        return map[a] - map[b]
    })
    return A
}

export function levDist(s: string, t: string): string {
    var d = [] //2d matrix

    // Step 1
    var n = s.length
    var m = t.length

    if (n == 0) return m
    if (m == 0) return n

    //Create an array of arrays in javascript (a descending loop is quicker)
    for (var i = n; i >= 0; i--) d[i] = []

    // Step 2
    for (var i = n; i >= 0; i--) d[i][0] = i
    for (var j = m; j >= 0; j--) d[0][j] = j

    // Step 3
    for (var i = 1; i <= n; i++) {
        var s_i = s.charAt(i - 1)

        // Step 4
        for (var j = 1; j <= m; j++) {

            //Check the jagged ld total so far
            if (i == j && d[i][j] > 4) return n

            var t_j = t.charAt(j - 1)
            var cost = (s_i == t_j) ? 0 : 1 // Step 5

            //Calculate the minimum
            var mi = d[i - 1][j] + 1
            var b = d[i][j - 1] + 1
            var c = d[i - 1][j - 1] + cost

            if (b < mi) mi = b
            if (c < mi) mi = c

            d[i][j] = mi // Step 6

            //Damerau transposition
            if (i > 1 && j > 1 && s_i == t.charAt(j - 2) && s.charAt(i - 2) == t_j) {
                d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + cost)
            }
        }
    }

    // Step 7
    return d[n][m]
}

export function shuffle(array) {
    var currentIndex = array.length, temporaryValue, randomIndex
    // While there remain elements to shuffle...
    while (0 !== currentIndex) {

        // Pick a remaining element...
        randomIndex = Math.floor(Math.random() * currentIndex)
        currentIndex -= 1

        // And swap it with the current element.
        temporaryValue = array[currentIndex]
        array[currentIndex] = array[randomIndex]
        array[randomIndex] = temporaryValue
    }

    return array
}

export function moveElement<T>(arr: T[], fromIndex: number, toIndex: number) {
    const reordered = [...arr]
    const elem = reordered.splice(fromIndex, 1)[0]
    reordered.splice(toIndex, 0, elem)
    return reordered
}

/**
 * Timing
 */
export function shake(obj: HTMLElement, delay: number = null) {
    obj.classList.toggle('animate__animated', false)
    obj.classList.toggle('animate__headShake', false)
    if (delay) {
        setTimeout(() => {
            obj.classList.toggle('animate__animated', true)
            obj.classList.toggle('animate__headShake', true)
        }, delay)
    } else {
        obj.classList.toggle('animate__animated', true)
        obj.classList.toggle('animate__headShake', true)
    }
}

interface TickerOptions {
    beforeTick: () => void
    onTick: () => void
    afterTick: () => void
}

class Ticker {
    constructor(interval: number, options: TickerOptions = {}) {
        this.beforeTick = options.beforeTick
        this.onTick = options.onTick
        this.afterTick = options.afterTick

        this.tickTimeout = null
        this.lastTick = null
        this.dt = 0
        this.interval = interval
        this.tick = this.tick.bind(this)
    }

    startTick(reset = true) {
        if (!reset && this.tickInterval != null) {
            return
        }
        clearTimeout(this.tickInterval)
        this.lastTick = Date.now()
        this.tickInterval = setTimeout(this.tick, this.interval)
    }

    tick() {
        if (this.beforeTick != null && !this.beforeTick()) {
            return
        }

        let now = Date.now()
        let dt = (now - this.lastTick) / 1000
        dt = (this.dt + dt) / 2
        var ret = null
        if (this.onTick) {
            ret = this.onTick(dt)
        }
        this.lastTick = now
        this.dt = dt

        if (this.afterTick && this.afterTick(ret)) {
            this.tickInterval = setTimeout(this.tick, this.interval)
        } else {
            this.tickInterval = null
        }
    }
}

/**
 * Encryption
 */
export function md5(d: string) {
    function M(d) { for (var _, m = "0123456789ABCDEF", f = "", r = 0; r < d.length; r++)_ = d.charCodeAt(r), f += m.charAt(_ >>> 4 & 15) + m.charAt(15 & _); return f }
    function X(d) { for (var _ = Array(d.length >> 2), m = 0; m < _.length; m++)_[m] = 0; for (m = 0; m < 8 * d.length; m += 8)_[m >> 5] |= (255 & d.charCodeAt(m / 8)) << m % 32; return _ }
    function V(d) { for (var _ = "", m = 0; m < 32 * d.length; m += 8)_ += String.fromCharCode(d[m >> 5] >>> m % 32 & 255); return _ }
    function Y(d, _) { d[_ >> 5] |= 128 << _ % 32, d[14 + (_ + 64 >>> 9 << 4)] = _; for (var m = 1732584193, f = -271733879, r = -1732584194, i = 271733878, n = 0; n < d.length; n += 16) { var h = m, t = f, g = r, e = i; f = md5_ii(f = md5_ii(f = md5_ii(f = md5_ii(f = md5_hh(f = md5_hh(f = md5_hh(f = md5_hh(f = md5_gg(f = md5_gg(f = md5_gg(f = md5_gg(f = md5_ff(f = md5_ff(f = md5_ff(f = md5_ff(f, r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 0], 7, -680876936), f, r, d[n + 1], 12, -389564586), m, f, d[n + 2], 17, 606105819), i, m, d[n + 3], 22, -1044525330), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 4], 7, -176418897), f, r, d[n + 5], 12, 1200080426), m, f, d[n + 6], 17, -1473231341), i, m, d[n + 7], 22, -45705983), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 8], 7, 1770035416), f, r, d[n + 9], 12, -1958414417), m, f, d[n + 10], 17, -42063), i, m, d[n + 11], 22, -1990404162), r = md5_ff(r, i = md5_ff(i, m = md5_ff(m, f, r, i, d[n + 12], 7, 1804603682), f, r, d[n + 13], 12, -40341101), m, f, d[n + 14], 17, -1502002290), i, m, d[n + 15], 22, 1236535329), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 1], 5, -165796510), f, r, d[n + 6], 9, -1069501632), m, f, d[n + 11], 14, 643717713), i, m, d[n + 0], 20, -373897302), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 5], 5, -701558691), f, r, d[n + 10], 9, 38016083), m, f, d[n + 15], 14, -660478335), i, m, d[n + 4], 20, -405537848), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 9], 5, 568446438), f, r, d[n + 14], 9, -1019803690), m, f, d[n + 3], 14, -187363961), i, m, d[n + 8], 20, 1163531501), r = md5_gg(r, i = md5_gg(i, m = md5_gg(m, f, r, i, d[n + 13], 5, -1444681467), f, r, d[n + 2], 9, -51403784), m, f, d[n + 7], 14, 1735328473), i, m, d[n + 12], 20, -1926607734), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 5], 4, -378558), f, r, d[n + 8], 11, -2022574463), m, f, d[n + 11], 16, 1839030562), i, m, d[n + 14], 23, -35309556), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 1], 4, -1530992060), f, r, d[n + 4], 11, 1272893353), m, f, d[n + 7], 16, -155497632), i, m, d[n + 10], 23, -1094730640), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 13], 4, 681279174), f, r, d[n + 0], 11, -358537222), m, f, d[n + 3], 16, -722521979), i, m, d[n + 6], 23, 76029189), r = md5_hh(r, i = md5_hh(i, m = md5_hh(m, f, r, i, d[n + 9], 4, -640364487), f, r, d[n + 12], 11, -421815835), m, f, d[n + 15], 16, 530742520), i, m, d[n + 2], 23, -995338651), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 0], 6, -198630844), f, r, d[n + 7], 10, 1126891415), m, f, d[n + 14], 15, -1416354905), i, m, d[n + 5], 21, -57434055), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 12], 6, 1700485571), f, r, d[n + 3], 10, -1894986606), m, f, d[n + 10], 15, -1051523), i, m, d[n + 1], 21, -2054922799), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 8], 6, 1873313359), f, r, d[n + 15], 10, -30611744), m, f, d[n + 6], 15, -1560198380), i, m, d[n + 13], 21, 1309151649), r = md5_ii(r, i = md5_ii(i, m = md5_ii(m, f, r, i, d[n + 4], 6, -145523070), f, r, d[n + 11], 10, -1120210379), m, f, d[n + 2], 15, 718787259), i, m, d[n + 9], 21, -343485551), m = safe_add(m, h), f = safe_add(f, t), r = safe_add(r, g), i = safe_add(i, e) } return Array(m, f, r, i) } function md5_cmn(d, _, m, f, r, i) { return safe_add(bit_rol(safe_add(safe_add(_, d), safe_add(f, i)), r), m) }
    function md5_ff(d, _, m, f, r, i, n) { return md5_cmn(_ & m | ~_ & f, d, _, r, i, n) }
    function md5_gg(d, _, m, f, r, i, n) { return md5_cmn(_ & f | m & ~f, d, _, r, i, n) }
    function md5_hh(d, _, m, f, r, i, n) { return md5_cmn(_ ^ m ^ f, d, _, r, i, n) }
    function md5_ii(d, _, m, f, r, i, n) { return md5_cmn(m ^ (_ | ~f), d, _, r, i, n) }
    function safe_add(d, _) { var m = (65535 & d) + (65535 & _); return (d >> 16) + (_ >> 16) + (m >> 16) << 16 | 65535 & m }
    function bit_rol(d, _) { return d << _ | d >>> 32 - _ }
    var r = M(V(Y(X(d), 8 * d.length)))
    return r.toLowerCase()
}

export function encrypt(text, key = 'uncrackable@123') {
    var encrypted = CryptoJS.AES.encrypt(JSON.stringify(text), key).toString()
    return encrypted
}

function decrypt(text, key = "uncrackable@123") {
    var bytes = CryptoJS.AES.decrypt(text, key)
    var decrypted = JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
    return decrypted
}

/**
 * Animation
 */
export function animateValue(start, end, duration, onUpdate, ease = 'easeOutCubic') {
    if (start === end) return Promise.resolve()
    return new Promise((resolve, reject) => {
        try {
            var stepTime = 1000 / 60
            const startTime = Date.now()
            const updatePosition = (time) => {
                let result = start
                switch (ease) {
                    default:
                        result = lerp(start, end, time)
                        onUpdate(current)
                        break
                    case 'easeOutCubic':
                        result = lerp(start, end, easeOutCubic(time))
                        break
                    // TODO, more functions, as needed

                }
                onUpdate(result)
            }
            updatePosition(0)
            var timer = setInterval(function () {
                const current = Math.min((Date.now() - startTime) / duration, 1)
                updatePosition(current)
                if (current >= 1) {
                    clearInterval(timer)
                    resolve()
                }
            }, stepTime)
        } catch (e) {
            reject(e)
        }
    })
}

function easeOutCubic(x: number): number {
    return 1 - Math.pow(1 - x, 3)
}

export function orderSort(a, b) {
    return a.order - b.order
}

/**
 * Cookies
 */
export function getCookie(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(';').shift()
    return null
}

export function matchCookie(prefix) {
    const value = `; ${document.cookie}`
    const parts = value.split('; ')
    const matches = parts.filter(part => part.startsWith(prefix))
    return matches
}

export function setCookie(name, value, days = 365) {
    const date = new Date()
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000))
    document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`
}

// Hack away some warnings
export const fnc = 0

